import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { IconAlertSquareRounded, IconInfoCircle, IconEye, IconEyeOff } from '@tabler/icons-react';
import { signUp } from 'aws-amplify/auth';
import ConfirmSignUp from '../ConfirmSignUp/ConfirmSignUp';
import PropTypes from 'prop-types';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import userApiService from '../../../services/oraganisation-api.service';
import { toast } from 'react-toastify';
import NPILookup from '../../NPILookUp/NPILookUp';
import PasswordRequirements from '../../PasswordRequirements/PasswordRequirements';

const BusinessRegisterSchema = Yup.object().shape({
  title: Yup.string().required('Role field is required'),
  businessName: Yup.string()
    .min(2, 'Organization name is too short')
    .max(50, 'Organization name is too long')
    .required('Organization name field is required'),
  fullName: Yup.string()
    .matches(
      /^[a-zA-Z\s-]+$/,
      'Full name can only contain letters, spaces, and hyphens',
    )
    .required('Full name field is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email address field is required'),
  orgNpiNumber: Yup.string()
    .when('title', {
      is: (val) => val === 'PROVIDER',
      then: (schema) =>
        schema.required('NPI number is required for providers.'),
      otherwise: (schema) => schema.nullable().optional(),
    })
    .matches(
      /^\d{10}$/,
      'NPI number must be exactly 10 digits and contain only numbers',
    )
    .nullable()
    .optional(),
  selectedNpi: Yup.object()
    .nullable()
    .when('orgNpiNumber', {
      is: (val) => !!val, // If there's a value in orgNpiNumber
      then: (schema) =>
        schema.required('Please select a valid NPI number from suggestions'),
      otherwise: (schema) => schema.nullable().optional(),
    }),
  password: Yup.string()
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      'Password must contain at least one special character',
    )
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password field is required'),
  termsAccepted: Yup.boolean()
    .oneOf([true], 'You must agree to the Terms of Use and Privacy Policy')
    .required('You must agree to the Terms of Use and Privacy Policy'),
});

function BusinessRegister({ priceID }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [nextStep, setNextStep] = useState(null);
  const [userCredentials, setUserCredentials] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/landing');
    }
  }, [isAuthenticated, navigate]);

  // Fetch available roles from API
  useEffect(() => {
    const fetchRoles = async () => {
      setIsLoadingRoles(true);
      try {
        const roles = await userApiService.getUserRoleTypes();

        setAvailableRoles(roles.user_types_list);
      } catch (error) {
        toast.error(
          'Error while fetching available user roles. Please try again later.',
        );
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  const generateUserAttributes = (values) => {
    const attributes = {
      email: values.email,
      name: values.fullName,
      'custom:org_name': values.businessName,
      'custom:org_user_title': values.title,
      'custom:org_npi_number': values.selectedNpi?.npi ?? '',
      'custom:signin_type': 'BUSINESS',
      'custom:user_type': 'ORG_OWNER',
    };

    if (priceID) {
      attributes['custom:selected_price_id'] = priceID;
    }

    return attributes;
  };

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    const userAttributes = generateUserAttributes(values);
    try {
      const { nextStep } = await signUp({
        username: values.email,
        password: values.password,
        options: {
          userAttributes,
          autoSignIn: true,
        },
      });

      setNextStep(nextStep);
      setUserCredentials({
        username: values.email,
        password: values.password,
        email: values.email,
      });
    } catch (err) {
      let errorMessage = 'An unexpected error occurred. Please try again.';

      switch (err.name) {
        case 'UsernameExistsException':
          errorMessage = 'An account with this email address already exists';
          break;
        case 'InvalidParameterException':
          errorMessage = 'Invalid registration parameters';
          break;
        case 'InvalidPasswordException':
          errorMessage = 'Password does not meet requirements';
          break;
        case 'UserLambdaValidationException':
          errorMessage = err.message.split('|')[1];
          break;
        default:
          errorMessage = 'An unexpected error occurred. Please try again.';
      }

      setStatus({ error: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  if (nextStep?.signUpStep === 'CONFIRM_SIGN_UP' && userCredentials) {
    return (
      <ConfirmSignUp
        userCredentials={userCredentials}
        onGoBack={() => {
          setNextStep(null);
          setUserCredentials(null);
        }}
      />
    );
  }

  return (
    <>
      <Formik
        initialValues={{
          businessName: '',
          fullName: '',
          title: '',
          email: '',
          orgNpiNumber: '',
          selectedNpi: '',
          password: '',
          confirmPassword: '',
          termsAccepted: false,
        }}
        initialTouched={{ selectedNpi: true }}
        validationSchema={BusinessRegisterSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          status,
          isSubmitting,
          values,
          setFieldTouched,
          setFieldValue,
          validateField,
        }) => (
          <>
            <Form className="cst-form">
              <h2 className="h4 fw-bold mb-4 text-center">
                Create Your Account
              </h2>
              <h2 className="fs-6 fw-bolder">Organization Details</h2>
              <div className="mb-lg-4 mb-3">
                <label
                  htmlFor="businessName"
                  className="pb-1 small fw-semibold"
                >
                  Organization name*
                </label>
                <Field
                  type="text"
                  className={`form-control ${touched.businessName && errors.businessName ? 'is-invalid border-danger' : ''}`}
                  id="businessName"
                  name="businessName"
                  disabled={isSubmitting}
                  autoComplete="off"
                  placeholder="Organization Name"
                />
                {touched.businessName && errors.businessName && (
                  <div className="invalid-feedback ">{errors.businessName}</div>
                )}
              </div>
              <h2 className="fs-6 fw-bolder">User Information</h2>

              <div className="mb-lg-4 mb-3">
                <label htmlFor="fullName" className="pb-1 small fw-semibold">
                  Full name*
                </label>
                <Field
                  type="text"
                  className={`form-control ${touched.fullName && errors.fullName ? 'is-invalid border-danger' : ''}`}
                  id="fullName"
                  name="fullName"
                  disabled={isSubmitting}
                  autoComplete="off"
                  placeholder="Joseph Smith"
                />
                {touched.fullName && errors.fullName && (
                  <div className="invalid-feedback ">{errors.fullName}</div>
                )}
              </div>

              <div className="mb-lg-4 mb-3">
                <label htmlFor="title" className="pb-1 small fw-semibold">
                  Title or role
                </label>
                <Field
                  as="select"
                  className={`form-select ${touched.title && errors.title ? 'is-invalid border-danger' : ''}`}
                  id="title"
                  name="title"
                  onChange={(e) => {
                    setFieldTouched('title');
                    setFieldValue('title', e.target.value).then(() => {
                      validateField('title');
                    });

                    setFieldValue('orgNpiNumber', '');
                    setFieldValue('selectedNpi', '');
                  }}
                  disabled={isSubmitting || isLoadingRoles}
                >
                  <option value="">Select your role</option>
                  {availableRoles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.name}
                    </option>
                  ))}
                </Field>
                {isLoadingRoles && (
                  <div className="form-text">Loading available roles...</div>
                )}
                {touched.title && errors.title && (
                  <div className="invalid-feedback">{errors.title}</div>
                )}
              </div>

              {/* NPILookup Component */}
              {values.title === 'PROVIDER' && (
                <NPILookup
                  name="orgNpiNumber"
                  label="NPI Number"
                  disabled={isSubmitting}
                >
                  {touched.selectedNpi && errors.selectedNpi && (
                    <div className="invalid-feedback d-block">
                      {errors.selectedNpi}
                    </div>
                  )}
                </NPILookup>
              )}

              <div className="mb-lg-4 mb-3">
                <label htmlFor="email" className="pb-1 small fw-semibold">
                  Email address*
                </label>
                <Field
                  type="email"
                  className={`form-control ${touched.email && errors.email ? 'is-invalid border-danger' : ''}`}
                  id="email"
                  name="email"
                  disabled={isSubmitting}
                  autoComplete="off"
                  placeholder="name@example.com"
                />
                {touched.email && errors.email && (
                  <div className="invalid-feedback ">{errors.email}</div>
                )}
              </div>
              <h2 className="fs-6 fw-bolder d-flex align-items-center">
                Set Password{' '}
                <OverlayTrigger
                  placement="right"
                  trigger={['hover', 'focus']}
                  overlay={
                    <Popover
                      id="password-popover"
                      className="custom-popover mx-2"
                    >
                      <Popover.Header as="h3" className="bg-light">
                        Password Requirements
                      </Popover.Header>
                      <Popover.Body>
                        <div className="text-start">
                          <ul className="mb-1 ps-3">
                            <li>Minimum 8 characters</li>
                            <li>At least one uppercase letter (A–Z)</li>
                            <li>At least one lowercase letter (a–z)</li>
                            <li>At least one number (0–9)</li>
                            <li>At least one special character (!@#$%^&*)</li>
                          </ul>
                        </div>
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <div className="d-inline-block px-1">
                    <IconInfoCircle
                      size={20}
                      className="text-secondary cursor-pointer"
                    />
                  </div>
                </OverlayTrigger>
              </h2>

              <div className="mb-lg-4 mb-3">
                <label htmlFor="password" className="pb-1 small fw-semibold">
                  Password*
                </label>

                <div className="input-with-icon create-pass">
                  <Field
                    type={showPassword ? 'text' : 'password'}
                    className={`form-control pr-12 ${touched.password && errors.password ? 'is-invalid border-danger' : ''}`}
                    id="password"
                    name="password"
                    disabled={isSubmitting}
                    autoComplete="off"
                    placeholder="Password"
                    onChange={(e) => {
                      setFieldTouched('password');
                      setFieldValue('password', e.target.value);
                    }}
                  />
                  {values.password && (
                    <span
                      className="password-toggle-icon"
                      onClick={() => {
                        if (!isSubmitting) {
                          setShowPassword((prev) => !prev);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {showPassword ? (
                        <IconEyeOff size={20} />
                      ) : (
                        <IconEye size={20} />
                      )}
                    </span>
                  )}
                </div>
                {values.password && (
                  <PasswordRequirements 
                    password={values.password} 
                    className="mt-3"
                  />
                )}
                {touched.password && errors.password && (
                  <div className="invalid-feedback d-block">{errors.password}</div>
                )}
              </div>

              <div className="mb-lg-4 mb-3">
                <label
                  htmlFor="confirmPassword"
                  className="pb-1 small fw-semibold"
                >
                  Confirm Password*
                </label>
                <div className="input-with-icon create-pass">
                  <Field
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`form-control pr-12 ${touched.confirmPassword && errors.confirmPassword ? 'is-invalid border-danger' : ''}`}
                    id="confirmPassword"
                    name="confirmPassword"
                    disabled={isSubmitting}
                    autoComplete="off"
                    placeholder="Password"
                  />
                  {values.confirmPassword && (
                    <span
                      className="password-toggle-icon"
                      onClick={() => {
                        if (!isSubmitting) {
                          setShowConfirmPassword((prev) => !prev);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {showConfirmPassword ? (
                        <IconEyeOff size={20} />
                      ) : (
                        <IconEye size={20} />
                      )}
                    </span>
                  )}
                </div>
                {touched.confirmPassword && errors.confirmPassword && (
                  <div className="invalid-feedback d-block">
                    {errors.confirmPassword}
                  </div>
                )}
              </div>

              <div className="mb-lg-4 mb-3">
                <div className="d-flex align-items-start gap-2">
                  <Field
                    type="checkbox"
                    name="termsAccepted"
                    id="termsAccepted"
                    disabled={isSubmitting}
                    className={`cursor-pointer mt-1 ${
                      touched.termsAccepted && errors.termsAccepted 
                        ? 'is-invalid' 
                        : ''
                    }`}
                  />
                  <label
                    htmlFor="termsAccepted"
                    className="text-sm cursor-pointer"
                    style={{ lineHeight: '1.5' }}
                  >
                    By creating an account, I agree to PrescriptSure's{' '}
                    <a
                      href={`${process.env.REACT_APP_PUBLIC_WEBSITE_URL}/terms-of-use`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-none hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms of Use
                    </a>
                    ,{' '}
                    <a
                      href={`${process.env.REACT_APP_PUBLIC_WEBSITE_URL}/privacy-policy`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-none hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </a>
                    ,{' '}
                    <a
                      href={`${process.env.REACT_APP_PUBLIC_WEBSITE_URL}/cookie-policy`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-none hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Cookie Policy
                    </a>
                    , and{' '}
                    <a
                      href={`${process.env.REACT_APP_PUBLIC_WEBSITE_URL}/accessibility-policy`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-none hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Accessibility Policy
                    </a>
                    .
                  </label>
                </div>
                {touched.termsAccepted && errors.termsAccepted && (
                  <div className="text-danger small mt-1">
                    {errors.termsAccepted}
                  </div>
                )}
              </div>

              {status && status.error && (
                <div className="alert alert-danger" role="alert">
                  <span className="px-2">
                    <IconAlertSquareRounded />
                  </span>
                  {status.error}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span 
                      className="spinner-border spinner-border-sm me-2" 
                      role="status" 
                      aria-hidden="true"
                    />
                    <span>Registering...</span>
                  </>
                ) : (
                  'Register Account'
                )}
              </button>
            </Form>
            <div className="mt-4 text-center">
              <p className="text-muted small">
                Already have an account?{' '}
                <Link
                  to="/"
                  onClick={(e) => isSubmitting && e.preventDefault()}
                  aria-disabled={isSubmitting}
                  tabIndex={isSubmitting ? -1 : 0}
                  className="text-primary fw-medium text-decoration-none"
                >
                  Login here
                </Link>
              </p>
            </div>
          </>
        )}
      </Formik>
    </>
  );
}

BusinessRegister.propTypes = {
  priceID: PropTypes.string,
};

export default BusinessRegister;
